// PageErrorFallback.tsx — constitution §Gestion des erreurs
// Gère les cas d'erreur : 401/403/404/5xx avec messages explicites en français.

import { AlertTriangle, Lock, Search, WifiOff } from "lucide-react";

import { cn } from "@/shared/utils/cn";

interface PageErrorFallbackProps {
  status?: number;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

interface ErrorConfig {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  showRetry: boolean;
}

function getErrorConfig(status?: number): ErrorConfig {
  switch (status) {
    case 401:
      return {
        icon: Lock,
        title: "Session expirée",
        description: "Votre session a expiré. Reconnectez-vous.",
        showRetry: false,
      };
    case 403:
      return {
        icon: Lock,
        title: "Accès refusé",
        description: "Vous n'avez pas accès à cette ressource.",
        showRetry: false,
      };
    case 404:
      return {
        icon: Search,
        title: "Page introuvable",
        description: "La ressource demandée est introuvable.",
        showRetry: false,
      };
    default:
      if (status !== undefined && status >= 500 && status <= 599) {
        return {
          icon: AlertTriangle,
          title: "Erreur serveur",
          description: "Une erreur serveur est survenue. Réessayez plus tard.",
          showRetry: true,
        };
      }
      return {
        icon: status === 0 ? WifiOff : AlertTriangle,
        title: status === 0 ? "Erreur réseau" : "Erreur inattendue",
        description:
          status === 0
            ? "Vérifiez votre connexion internet et réessayez."
            : "Une erreur est survenue. Réessayez ou contactez le support.",
        showRetry: true,
      };
  }
}

export function PageErrorFallback({
  status,
  message,
  onRetry,
  className,
}: PageErrorFallbackProps) {
  const config = getErrorConfig(status);
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 py-16 text-center",
        className,
      )}
      role="alert"
    >
      <Icon className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{config.title}</h2>
        <p className="text-sm text-muted-foreground">
          {message ?? config.description}
        </p>
      </div>
      {config.showRetry && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Réessayer"
        >
          Réessayer
        </button>
      )}
    </div>
  );
}
